/**
 * How often a tracked job or spray keeps a GPS fix.
 *
 * A fixed clock is wrong both ways. 30 s suits a pruning crew walking a block
 * and is useless for a 2 m mower at 13 km/h: that is 108 m between points,
 * one or two per row, with every row-end turn cut off. A 0.8 ha job at that
 * width is ~4 km of driving, done in under 20 minutes, so it would finish with
 * a few dozen points and a coverage figure built from chords.
 *
 * So the rule is **distance and turns, bounded by time**:
 *   - keep a fix once the machine has gone `spacingM` since the last one, so a
 *     fast machine records often and a slow one rarely, adjusting fix by fix
 *     with no warm-up to get wrong;
 *   - keep one where it turns (`turnDeg`), so row ends are on the track;
 *   - never closer than `minIntervalMs`, and at least every `maxIntervalMs`
 *     while the platform is delivering fixes.
 *
 * `spacingM` comes from the work: narrower implements and smaller jobs need
 * finer tracks. What it saves is points stored and uploaded; the GPS itself
 * runs for as long as tracking does. `uploadEveryMs` paces the sends, which
 * is where the radio's battery goes.
 */

export interface CadenceHints {
  /** Working width of the implement, metres. */
  workingWidthM?: number | string | null;
  /** Area the job is meant to cover, hectares. */
  targetAreaHa?: number | string | null;
}

export interface CadencePlan {
  /** spray: tank checks read the track. coverage: a width to sweep. trail: where people went. */
  kind: 'spray' | 'coverage' | 'trail';
  /** Keep a fix once this far from the last kept one. */
  spacingM: number;
  /** Never keep two fixes closer in time than this. */
  minIntervalMs: number;
  /** Keep a fix at least this often while fixes are arriving. */
  maxIntervalMs: number;
  /** Keep a fix where the heading has moved this far; null for no turn rule. */
  turnDeg: number | null;
  /** Movement the platform waits for before it reports a fix at all. */
  distanceFilterM: number;
  /**
   * How often kept points are sent while tracking. Each send wakes the mobile
   * radio, so this, not the point count, is what uploads cost in battery.
   */
  uploadEveryMs: number;
}

export interface CadenceFix {
  lat: number;
  lng: number;
  /** ms since epoch, when the fix reached us. */
  at: number;
  /** m/s from the device, if it has one. */
  speed?: number | null;
  /** Degrees from north, from the device, if it has one. */
  heading?: number | null;
}

/** The last kept fix, carried between calls. */
export interface KeptFix {
  lat: number;
  lng: number;
  at: number;
  /** Direction of travel when it was kept, or null if not known. */
  heading: number | null;
}

/**
 * A spray's per-tank area counts ground within 15 m of a fix, so fixes must
 * stay inside that 30 m band; 15 m apart leaves margin. Its ceiling is the
 * 10 s it recorded at before (2026-09-29), so a slow spray is never sparser.
 */
const SPRAY_SPACING_M = 15;
const SPRAY_MAX_INTERVAL_MS = 10_000;

/**
 * A coverage job keeps a fix every five implement widths, between 8 and 20 m.
 * Its ceiling is the 30 s every job used before, so it is never sparser.
 */
const COVERAGE_WIDTHS_PER_FIX = 5;
const COVERAGE_MIN_SPACING_M = 8;
const COVERAGE_MAX_SPACING_M = 20;
const COVERAGE_MAX_INTERVAL_MS = 30_000;
/**
 * A short job still gets a usable track: at least this many fixes over the
 * driving its area and width imply, down to `SHORT_JOB_FLOOR_M` apart.
 */
const SHORT_JOB_MIN_FIXES = 400;
const SHORT_JOB_FLOOR_M = 4;

/**
 * No width: pruning, hand thinning, walking a block. Where people went is the
 * whole story, and under 120 s between fixes keeps the server's area
 * integration from reading the gaps as pauses.
 */
const TRAIL_SPACING_M = 25;
const TRAIL_MIN_INTERVAL_MS = 5_000;
const TRAIL_MAX_INTERVAL_MS = 60_000;

const MIN_INTERVAL_MS = 2_000;

/**
 * Sends. A supervisor watching a mower or a spray sees it within two minutes;
 * the job's live panel polls every 30 s, so it moves in steps of about that.
 * A trail is only read afterwards. A finished tank and a finished job send
 * everything first regardless (`waypointQueue.sendNow` / `uploadAll`).
 */
const WORK_UPLOAD_MS = 120_000;
const TRAIL_UPLOAD_MS = 300_000;
const TURN_DEG = 25;
/** Below ~5 km/h a heading is mostly GPS noise; the turn rule waits. */
const TURN_MIN_SPEED_MS = 1.4;
/** A turn must also have moved, or jitter on the spot reads as one. */
const TURN_MIN_MOVE_M = 3;

function num(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function filterFor(spacingM: number): number {
  // A third of the spacing, so the platform offers several fixes per kept one
  // and a turn can be caught between them.
  return clamp(Math.round(spacingM / 3), 2, 10);
}

export function planCadence(type: 'job' | 'spray', hints: CadenceHints = {}): CadencePlan {
  if (type === 'spray') {
    return {
      kind: 'spray',
      spacingM: SPRAY_SPACING_M,
      minIntervalMs: MIN_INTERVAL_MS,
      maxIntervalMs: SPRAY_MAX_INTERVAL_MS,
      turnDeg: TURN_DEG,
      distanceFilterM: filterFor(SPRAY_SPACING_M),
      uploadEveryMs: WORK_UPLOAD_MS,
    };
  }

  const width = num(hints.workingWidthM);
  if (width == null) {
    return {
      kind: 'trail',
      spacingM: TRAIL_SPACING_M,
      minIntervalMs: TRAIL_MIN_INTERVAL_MS,
      maxIntervalMs: TRAIL_MAX_INTERVAL_MS,
      turnDeg: null,
      distanceFilterM: filterFor(TRAIL_SPACING_M),
      uploadEveryMs: TRAIL_UPLOAD_MS,
    };
  }

  let spacing = clamp(width * COVERAGE_WIDTHS_PER_FIX, COVERAGE_MIN_SPACING_M, COVERAGE_MAX_SPACING_M);
  const area = num(hints.targetAreaHa);
  if (area != null) {
    const travelM = (area * 10_000) / width;
    spacing = Math.min(spacing, Math.max(SHORT_JOB_FLOOR_M, travelM / SHORT_JOB_MIN_FIXES));
  }
  spacing = Math.round(spacing * 10) / 10;

  return {
    kind: 'coverage',
    spacingM: spacing,
    minIntervalMs: MIN_INTERVAL_MS,
    maxIntervalMs: COVERAGE_MAX_INTERVAL_MS,
    turnDeg: TURN_DEG,
    distanceFilterM: filterFor(spacing),
    uploadEveryMs: WORK_UPLOAD_MS,
  };
}

const R = 6_371_000;
const rad = (d: number) => (d * Math.PI) / 180;

export function distanceM(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function bearingDeg(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const y = Math.sin(rad(b.lng - a.lng)) * Math.cos(rad(b.lat));
  const x =
    Math.cos(rad(a.lat)) * Math.sin(rad(b.lat)) -
    Math.sin(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.cos(rad(b.lng - a.lng));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function angleBetween(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Decide on one fix. Returns the new last-kept fix when it is kept, or null
 * when it is dropped (the caller keeps its old one).
 */
export function keepFix(plan: CadencePlan, last: KeptFix | null, fix: CadenceFix): KeptFix | null {
  const deviceHeading =
    fix.heading != null && Number.isFinite(fix.heading) && (fix.speed ?? 0) >= TURN_MIN_SPEED_MS
      ? fix.heading
      : null;

  if (!last) return { lat: fix.lat, lng: fix.lng, at: fix.at, heading: deviceHeading };

  const dt = fix.at - last.at;
  if (dt < plan.minIntervalMs) return null;

  const d = distanceM(last, fix);
  const travel = d >= TURN_MIN_MOVE_M ? bearingDeg(last, fix) : null;
  const heading = deviceHeading ?? travel ?? last.heading;
  const kept: KeptFix = { lat: fix.lat, lng: fix.lng, at: fix.at, heading };

  if (dt >= plan.maxIntervalMs) return kept;
  if (d >= plan.spacingM) return kept;

  if (plan.turnDeg != null && last.heading != null && d >= TURN_MIN_MOVE_M) {
    const speed = fix.speed ?? d / (dt / 1000);
    const now = deviceHeading ?? travel;
    if (speed >= TURN_MIN_SPEED_MS && now != null && angleBetween(now, last.heading) >= plan.turnDeg) {
      return kept;
    }
  }
  return null;
}

/** "a point every 10 m", for the tracker card. */
export function describeCadence(plan: CadencePlan): string {
  const m = plan.spacingM >= 10 ? Math.round(plan.spacingM) : plan.spacingM;
  return plan.turnDeg != null
    ? `Recording a point every ${m} m and at each turn`
    : `Recording a point every ${m} m`;
}
